/** "New version available · Reload" and "Ready to play offline" (06 §15.3). */
import { signal } from '@preact/signals';
import { t } from '@tarock/i18n';
import { useEffect } from 'preact/hooks';
import { openModals } from '../hud/modalState';
import { tableStore } from '../stores/table';
import { applyUpdate, pwaState } from './register';

const offlineShown = signal(false);
const OFFLINE_KEY = 'tarock.offlineReadyShown';

/** At a table the update prompt waits until the current game ends (`gameOver`, i.e. phase `scored`). */
function gameRunning(): boolean {
  const g = tableStore.display.value?.view.game;
  return !!g && g.phase !== 'scored';
}

/** The update toast; never reloads by itself. */
export function UpdatePrompt() {
  const { needRefresh, offlineReady } = pwaState.value;
  useEffect(() => {
    if (!offlineReady) {
      return;
    }
    try {
      if (localStorage.getItem(OFFLINE_KEY)) {
        return;
      }
      localStorage.setItem(OFFLINE_KEY, '1');
    } catch {
      // show it anyway
    }
    offlineShown.value = true;
    const id = setTimeout(() => {
      offlineShown.value = false;
    }, 5000);
    return () => {
      clearTimeout(id);
      offlineShown.value = false;
    };
  }, [offlineReady]);
  // behind an open modal (the score sheet, the menu) the toast is inert: the score sheet offers Reload itself
  const showUpdate = needRefresh && !gameRunning() && openModals.value === 0;
  if (!showUpdate && !offlineShown.value) {
    return null;
  }
  return (
    <div class="toast-stack update-stack" role="status" aria-live="polite">
      {showUpdate && (
        <div class="toast" data-testid="update-prompt">
          <span>{t('pwa.update')}</span>
          <button type="button" class="btn small primary" onClick={() => void applyUpdate()}>
            {t('pwa.reload')}
          </button>
        </div>
      )}
      {offlineShown.value && <div class="toast">{t('pwa.offlineReady')}</div>}
    </div>
  );
}
